import { Offer, OffersQueryDto } from '../model/offer.types';
import { OffersDto } from '~shared/api/api.types';
import { Configuration } from '~entities/configuration/model/configuration.types';
import { CartItem } from '~entities/session/model/session.types';
import { oneIdentifier } from '~shared/config/hostname';
import { Room } from '~entities/room/model/room.types';
import { BookingRoom } from '~processes/booking/model/booking.types';
import { LastMinutePackage } from '~entities/package/model/package.types';
import { queryClient } from '~shared/query-client';
import i18n, { Locale } from '~app/i18n';
import { configurationQueryOptions } from '~entities/configuration/api/configuration.queries';

export const transformBookingRoomToToOffersQueryDto = (
  bookingRoom: BookingRoom,
): OffersQueryDto => ({
  adults: bookingRoom.occupancy.adults,
  children: bookingRoom.occupancy.children,
  arrivalDay: bookingRoom.offer.arrivalDay,
  nights: bookingRoom.offer.nights,
  oneIdentifier,
});

export const transformLastMinutePackageToOffer = (
  lastMinutePackage: LastMinutePackage,
): Offer => ({
  type: 'LAST_MINUTE_PACKAGE',
  arrivalDay: lastMinutePackage.arrivalDay,
  nights: lastMinutePackage.duration,
});

export const transformOffersDtoToOffers = (offersDto: OffersDto): Offer[] => {
  return offersDto.offers.map(offer => ({
    type: offer.type,
    arrivalDay: offer.arrivalDay,
    departureDay: offer.departureDay,
    nights: offer.nights,
    adults: offer.adults,
    children: offer.children,
    price: offer.price,
    minimumPrice: offer.minimumPrice,
    overlayMessages: offer.overlayMessages,
    bookable: offer.bookable,
    roomId: offer.roomCategoryId,
    lastMinutePackageId: offer.lastMinutePackageId,
    packageId: offer.packageGroupId,
    packageCategoryId: offer.packageCategoryId,
    rateId: offer.rateId,
  }));
};

export const populateOfferWithRate =
  (configuration: Configuration) => (offer: Offer) => ({
    ...offer,
    rate: configuration.rates.find(({ id }) => id === offer.rateId),
  });

export const populateOffersWithRate = (offers: Offer[]) => {
  const configuration = queryClient.getQueryData(
    configurationQueryOptions(i18n.language as Locale).queryKey,
  );

  if (!configuration) return;

  return offers.map(populateOfferWithRate(configuration));
};

export const transformCartItemToBookingRoom = (
  cartItem: CartItem,
): BookingRoom => ({
  id: cartItem.id,
  cartItemId: cartItem.id,
  occupancy: {
    adults: cartItem.guests.reduce(
      (a, guest) => a + (guest.type === 'ADULT' ? 1 : 0),
      0,
    ),
    children: cartItem.guests
      .filter(guest => guest.type === 'CHILD')
      .map(({ childAge }) => childAge ?? 0),
  },
  offer: cartItem,
  offeringId:
    cartItem.type === 'LAST_MINUTE_PACKAGE'
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        cartItem.lastMinutePackageId!
      : cartItem.type === 'PACKAGE'
      ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        cartItem.packageId!
      : cartItem.roomId,
});

export const groupOffersByRooms = (
  configuration: Configuration,
  offers: Offer[],
) => {
  return offers?.reduce<(Room & { offers: Offer[] })[]>((acc, offer) => {
    if (!offer.roomId) return acc;
    const existingRoom = acc.find(({ id }) => id === offer.roomId);
    if (existingRoom) {
      existingRoom.offers.push(offer);
      return acc;
    }

    const room = configuration.rooms.find(({ id }) => id === offer.roomId);

    if (!room) return acc;

    acc.push({
      ...room,
      offers: [offer],
    });

    return acc;
  }, []);
};
